// Manager — First response (User Journeys & Service Design v2, Journey 01).
//
// The focused, single-purpose PATHWAY-SCOPED drafter: arrive → case context →
// tone picker → editable draft → send from your own account → confirmation.
// Deliberately NOT the full case-detail page — a manager hearing about a
// death, on their phone, should land exactly here with no orientation
// (principle 1: minimum effort).
//
// It is no longer what the home page's "Help me draft a response" card opens
// (PO 2026-08-06 — that card is the case-less `/manager/draft`, which assumes
// no pathway exists). This page is reached from that drafter's pathway picker,
// from the pathway detail page, and from email deep-links. The split is the
// governance line, not just navigation: THIS page knows who the message is
// about, so its draft is audited against the employee and its send stamps
// `first_response_sent_at`. The case-less one can do neither.
//
// Shares the `['cases', id]` query cache with the case-detail route, so the
// send-stamp invalidation flips this page from the drafter to the confirmation
// without a manual refetch. No device-type guard: the mobile Maeve-First entry
// deep-links straight here, and the layout is single-column / mobile-first.

import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import type {
  CaseDetailWithRtw,
  DeceasedRelationship,
  TouchpointSummary,
} from '@besideu/shared/cases';
import { api, ApiError } from '@/lib/api.ts';
import { Card } from '@/components/ui/Card.tsx';
import { MaeveAvatar } from '@/components/brand/MaeveAvatar.tsx';
import { CaseNotFound } from '@/components/CaseNotFound.tsx';
import { ManagerMaeveFirstResponse } from '@/components/ManagerMaeveFirstResponse.tsx';

export function ManagerFirstResponse() {
  const { id = '' } = useParams<{ id: string }>();
  const detail = useQuery({
    queryKey: ['cases', id],
    queryFn: () => api.get<CaseDetailWithRtw>(`/api/cases/${id}`),
    enabled: id !== '',
  });

  if (detail.isPending) return <p className="text-muted-foreground">Loading…</p>;
  if (detail.error) {
    if (detail.error instanceof ApiError && detail.error.status === 404) {
      return (
        <section className="mx-auto max-w-2xl space-y-6">
          <CaseNotFound
            backTo="/manager/team"
            reason="It may belong to another manager, or it may have been removed."
          />
        </section>
      );
    }
    return <p className="text-traffic-red">Could not load this support pathway.</p>;
  }

  const { case: c, touchpoints, employeePreferences } = detail.data;
  const sent = c.firstResponseSentAt !== null;

  return (
    <section className="mx-auto max-w-2xl space-y-6">
      <Link
        to={`/manager/cases/${id}`}
        className="text-sm text-muted-foreground underline-offset-4 hover:underline"
      >
        ← Back to the support pathway
      </Link>

      {/* Context strip (STEP 1) — Maeve already has the case context. */}
      <div className="flex items-center gap-4 rounded-2xl bg-ink px-5 py-4 text-white sm:px-6">
        <MaeveAvatar size={44} rings={false} />
        <div className="min-w-0">
          <p className="text-[0.68rem] font-semibold tracking-[0.16em] text-primary uppercase">
            First response
          </p>
          <p className="mt-0.5 font-heading text-lg font-bold">Responding to {c.employee.name}</p>
          <p className="text-sm text-white/70">
            Following the loss of {relationshipPhrase(c.deceasedRelationship)} · {c.dateOfLoss}
          </p>
        </div>
      </div>

      {sent ? (
        <SentConfirmation employeeName={c.employee.name} caseId={id} touchpoints={touchpoints} />
      ) : (
        // `key` forces a remount on caseId change — see the matching
        // comment in ManagerCaseDetail.tsx. Without it, a deep-link
        // navigation from one case's first-response page to another's
        // (same route pattern, no unmount) would leave the previous
        // case's drafted message on screen under the new context strip.
        <ManagerMaeveFirstResponse
          key={c.id}
          caseId={c.id}
          firstResponseSentAt={c.firstResponseSentAt}
          contactChannel={employeePreferences.visible.preferredContactChannel ?? null}
          contactPerson={employeePreferences.visible.preferredContactPerson ?? null}
          autoGenerate
        />
      )}
    </section>
  );
}

// STEP 6 — confirmation. What the manager needs to know is handled: HR has the
// case, the check-in schedule is running, and here's the next one.
function SentConfirmation({
  employeeName,
  caseId,
  touchpoints,
}: {
  employeeName: string;
  caseId: string;
  touchpoints: TouchpointSummary[];
}) {
  const firstName = employeeName.split(' ')[0] || employeeName;
  const nextCheckIn = touchpoints
    .filter((t) => t.completedAt === null && t.status !== 'Suppressed' && t.status !== 'Skipped')
    .sort((a, b) => a.scheduledFor.localeCompare(b.scheduledFor))[0];

  return (
    <Card className="space-y-4">
      <div>
        <h2 className="font-heading text-xl font-bold">That&rsquo;s the hardest part done.</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Your message is marked as sent — {firstName} will feel held rather than forgotten in the
          first hours, which is what matters most.
        </p>
      </div>
      <ul className="space-y-2 text-sm">
        <li className="flex items-start gap-2">
          <span aria-hidden className="mt-0.5 text-traffic-green-text">
            ✓
          </span>
          <span>HR has been notified and can see the support pathway is under way.</span>
        </li>
        <li className="flex items-start gap-2">
          <span aria-hidden className="mt-0.5 text-traffic-green-text">
            ✓
          </span>
          <span>
            {nextCheckIn ? (
              <>
                Your next check-in with {firstName} is scheduled for{' '}
                <span className="font-medium text-foreground">{nextCheckIn.scheduledFor}</span> —
                Maeve will remind you.
              </>
            ) : (
              <>The check-in schedule is set and Maeve will remind you when each one is due.</>
            )}
          </span>
        </li>
      </ul>
      <div className="flex flex-wrap gap-2">
        <Link
          to={`/manager/cases/${caseId}`}
          className="inline-flex items-center rounded-sm bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:opacity-90"
        >
          Open {firstName}&rsquo;s support pathway
        </Link>
        {/* `/manager/team`, not `/manager` — since PO 2026-08-06 the latter
            is the four action cards and the pathway list this promises lives
            on the Team tab. */}
        <Link
          to="/manager/team"
          className="inline-flex items-center rounded-sm border border-border px-4 py-2.5 text-sm font-medium hover:bg-muted"
        >
          Back to my team
        </Link>
      </div>
    </Card>
  );
}

const RELATIONSHIP_PHRASE: Record<DeceasedRelationship, string> = {
  Parent: 'their parent',
  Partner: 'their partner',
  Child: 'their child',
  Sibling: 'their sibling',
  Grandparent: 'their grandparent',
  Other: 'someone close to them',
};

function relationshipPhrase(rel: DeceasedRelationship): string {
  return RELATIONSHIP_PHRASE[rel];
}
